import {
  BookingDto,
  IExtendableDto,
  IRideDto,
  ReasonDtoDropdownItem,
} from "../dto";
import { DirectionEnum } from "../enums";
import { DateYMDString, TTimeHHmm } from "./date-time.models";
import { IGenericUploadEntityCreateFieldItem } from "./generic-upload/generic-upload.create-entity.models";
import { BookingEntity } from "./typeorm";

export enum BookingConflictErrorType {
  passengerIneligible = "passengerIneligible",
  passengerLocationIneligible = "passengerLocationIneligible",
  passengerTimeIneligible = "passengerTimeIneligible",
  noStationSet = "noStationSet",
  existingRide = "existingRide",
  gapSingleOverlappingBookingBToB = "gapSingleOverlappingBookingBToB",
  gapSingleOverlappingBookingBToA = "gapSingleOverlappingBookingBToA",
  gapSingleOverlappingBookingAToA = "gapSingleOverlappingBookingAToA",
  insideBooking = "insideBooking",
  overlappingBooking = "overlappingBooking",
  withinProductionLock = "withinProductionLock",
  shiftAvailability = "shiftAvailability",
  // RYDE-8171: a bulk row on a meal-only shift that cannot be booked - no meal the shift offers resolves,
  // or a meal-only (X) row sits on a regular shift. Reported per passenger; there is nothing to approve.
  mealOnlyShiftRequiresMeal = "mealOnlyShiftRequiresMeal",
  mealOnlyBookingShiftMismatch = "mealOnlyBookingShiftMismatch",
  statusMismatch = "statusMismatch",
}

export const IneligibleAdditionalErrorType = {
  [BookingConflictErrorType.passengerIneligible]: "passengerIneligible",
  [BookingConflictErrorType.passengerLocationIneligible]: "locationIneligible",
  [BookingConflictErrorType.passengerTimeIneligible]: "timeIneligible",
};

export enum ProduceJoinConflictsErrorType {
  missingPassenger = "missingPassenger",
  missingOriginArea = "missingOriginArea",
  missingDestinationArea = "missingDestinationArea",
  missingOriginStationInfo = "missingOriginStationInfo",
  missingDestinationStationInfo = "missingDestinationStationInfo",
  sameStationsFromTo = "sameStationsFromTo",
  hubSameAsBookingStation = "hubSameAsBookingStation",
  existingRide = "existingRide",
  incorrectStatus = "incorrectStatus",
  inactivePassenger = "inactivePassenger",
  invalidStationLocation = "invalidStationLocation",
  stationRouteNotFoundByProvider = "stationRouteNotFoundByProvider",
  stationRouteDurationUnavailable = "stationRouteDurationUnavailable",
  stationRouteLookupFailed = "stationRouteLookupFailed",
}

export enum ConflictKeysType {
  workerCode = "workerCode",
  bookingId = "bookingId",
  passengerId = "passengerId",
  passengerFullName = "passengerFullName",
  date = "date",
  errorType = "errorType",
  stationId = "stationId",
  stationName = "stationName",
}

export interface IBookingCalendarParsedCell {
  date: DateYMDString;
  passengerId: number;
}

export type BookingCellId = `${DateYMDString}_${number}`;

export interface IStationInfoConflicts {
  stationId: number;
  stationName: string;
}

export interface IConflict extends Partial<IStationInfoConflicts> {
  id: number;
  bookingId?: number;
  passengerId?: number;
  passengerFullName: string;
  displayDirection?: DirectionEnum;
  workerCode?: string;
  date: DateYMDString;
  errorType: BookingConflictErrorType;
}

export type ErrorAdditionalT =
  | "gapSingleOverlappingBooking"
  | "overlappingBooking"
  | "existingRide"
  | "insideBooking";

export type BulkBookingCreateConflict = Required<
  Omit<IConflict, "bookingId">
> & {
  bookingId?: number;
  errorAdditional?: Partial<Record<ErrorAdditionalT, BookingDto | IRideDto>>;
};

export interface IPassengerIneligibleAdditional {
  direction?: DirectionEnum;
}

export interface IWithinProductionLockAdditional {
  direction?: DirectionEnum;
}

export interface IIneligibleAdditional {
  ineligibleErrorType: (typeof IneligibleAdditionalErrorType)[keyof typeof IneligibleAdditionalErrorType];
}

export const isIPassengerIneligibleAdditional = (
  errorAdditional: unknown,
): errorAdditional is IPassengerIneligibleAdditional => {
  return (
    typeof errorAdditional === "object" &&
    errorAdditional !== null &&
    "direction" in errorAdditional
  );
};

export interface IApprovalBookingConflict {
  id: number;
  /** The booking the conflict is about. Only flows that edit existing bookings carry it. */
  bookingId?: number;
  passengerId: number;
  passengerFullName: string;
  date: DateYMDString;
  displayDirection?: DirectionEnum;
  errorType: BookingConflictErrorType;
  approvalReason: ReasonDtoDropdownItem | null;
  approver: string | null;
  errorAdditional?:
    | Partial<Record<ErrorAdditionalT, BookingDto | IRideDto>>
    | IPassengerIneligibleAdditional
    | IWithinProductionLockAdditional
    | IIneligibleAdditional;
}

export interface IApprovalSetBookingConflictAPI {
  id: number;
  /** Keys the approval to one booking, for flows where passenger plus date is not unique. */
  bookingId?: number;
  passengerId: number;
  approvalReasonId: number;
  approver: string;
  date: DateYMDString;
  direction?: DirectionEnum;
  displayDirection?: DirectionEnum;
}

export const BOOKING_BULK_FORM_BASE_FIELDS = {
  conflictsDetailsCreateIfExisting: "conflictsDetailsCreateIfExisting",
  conflictsDetailsCreateIfInside: "conflictsDetailsCreateIfInside",
  conflictsDetailsCreateIfOverlap: "conflictsDetailsCreateIfOverlap",
  conflictsDetailsCreateIfGap: "conflictsDetailsCreateIfGap",
  conflictsDetailsCreateIfIneligible: "conflictsDetailsCreateIfIneligible",
} as const;

export const BOOKING_BULK_FORM_FIELDS = {
  ...BOOKING_BULK_FORM_BASE_FIELDS,
  direction: "direction",
  arrivalTime: "arrivalTime",
  departureTime: "departureTime",
  shiftId: "shiftId",
  originStationIdA: "originStationIdA",
  destinationStationIdA: "destinationStationIdA",
  hubStationA: "hubStationA",
  originStationIdB: "originStationIdB",
  destinationStationIdB: "destinationStationIdB",
  hubStationB: "hubStationB",
  mealId: "mealId",
  isDraft: "isDraft",
  shouldReplaceExisting: "shouldReplaceExisting",
  flightId: "flightId",
} as const;

export type TBookingBulkFormFieldsNamesKeys =
  keyof typeof BOOKING_BULK_FORM_FIELDS;
export type TBookingBulkFormFieldsNames =
  (typeof BOOKING_BULK_FORM_FIELDS)[TBookingBulkFormFieldsNamesKeys];

export const BOOKING_ADD_NEW_FORM_FIELDS = {
  ...BOOKING_BULK_FORM_FIELDS,
  passengers: "passengers",
  date: "date",
} as const;
export type TBookingAddNewFormFieldsNamesKeys =
  keyof typeof BOOKING_ADD_NEW_FORM_FIELDS;
export type TBookingAddNewFormFieldsNames =
  (typeof BOOKING_ADD_NEW_FORM_FIELDS)[TBookingAddNewFormFieldsNamesKeys];

/** The wording that varies between the panels sharing the preview step and its footer. */
export interface IBulkBookingsPanelLabelKeys {
  /** The footer's primary button on the preview step, where the run is committed. */
  commit: string;
  /** Summary line before the run commits, and after it finished. */
  resultPending: string;
  resultFinished: string;
  /** Progress bar title once the run finished, when it failed, and while it is still going. */
  progressFinished: string;
  progressError: string;
  progressRunning: string;
  /** Section titles this panel words differently; an error type left out falls back to `booking.bulk.<errorType>`. */
  conflictTitles: Partial<Record<BookingConflictErrorType, string>>;
}

/**
 * The bulk date/time panel. `shiftId` is a shortcut that fills direction and time only - it is not
 * sent and never changes a booking's shift - and `direction` picks which bookings the run touches.
 */
export const BOOKING_DATE_TIME_UPDATE_FORM_FIELDS = {
  shiftId: "shiftId",
  direction: "direction",
  date: "date",
  time: "time",
} as const;

export interface IBulkUpdateBookingsDateTimeForm {
  [BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.shiftId]?: number;
  [BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.direction]: DirectionEnum;
  [BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.date]?: TDateISO | null;
  [BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.time]?: TTimeHHmm;
}

export interface IBulkCloneBookingsForm {
  dateFrom?: TDateISO;
  dateTo?: TDateISO;
  dayGroup?: number[];
  dayType?: number[];
}

export const BOOKING_UPLOAD_FORM_FIELDS = {
  ...BOOKING_BULK_FORM_BASE_FIELDS,
  file: "file",
  entityCreateFields: "entityCreateFields",
} as const;

export interface IBulkUploadBookingsForm {
  [BOOKING_UPLOAD_FORM_FIELDS.conflictsDetailsCreateIfExisting]?: string;
  [BOOKING_UPLOAD_FORM_FIELDS.conflictsDetailsCreateIfInside]?: string;
  [BOOKING_UPLOAD_FORM_FIELDS.conflictsDetailsCreateIfOverlap]?: string;
  [BOOKING_UPLOAD_FORM_FIELDS.conflictsDetailsCreateIfGap]?: boolean;
  [BOOKING_UPLOAD_FORM_FIELDS.conflictsDetailsCreateIfIneligible]?: string;
  [BOOKING_UPLOAD_FORM_FIELDS.entityCreateFields]: IGenericUploadEntityCreateFieldItem[];
  [BOOKING_UPLOAD_FORM_FIELDS.file]: File;
}

export interface IBulkSetBookingsForm {
  [BOOKING_BULK_FORM_FIELDS.direction]: DirectionEnum;
  [BOOKING_BULK_FORM_FIELDS.arrivalTime]?: TTimeHHmm;
  [BOOKING_BULK_FORM_FIELDS.departureTime]?: TTimeHHmm;
  [BOOKING_BULK_FORM_FIELDS.shiftId]?: number;
  [BOOKING_BULK_FORM_FIELDS.originStationIdA]?: number | null;
  [BOOKING_BULK_FORM_FIELDS.destinationStationIdA]?: number | null;
  [BOOKING_BULK_FORM_FIELDS.hubStationA]?: number | null;
  [BOOKING_BULK_FORM_FIELDS.originStationIdB]?: number | null;
  [BOOKING_BULK_FORM_FIELDS.destinationStationIdB]?: number | null;
  [BOOKING_BULK_FORM_FIELDS.hubStationB]?: number | null;
  [BOOKING_BULK_FORM_FIELDS.mealId]?: number;
  [BOOKING_BULK_FORM_FIELDS.isDraft]?: boolean;
  [BOOKING_BULK_FORM_FIELDS.shouldReplaceExisting]?: boolean;
  [BOOKING_BULK_FORM_FIELDS.flightId]?: number;
  [BOOKING_BULK_FORM_FIELDS.conflictsDetailsCreateIfExisting]?: string;
  [BOOKING_BULK_FORM_FIELDS.conflictsDetailsCreateIfInside]?: string;
  [BOOKING_BULK_FORM_FIELDS.conflictsDetailsCreateIfOverlap]?: string;
  [BOOKING_BULK_FORM_FIELDS.conflictsDetailsCreateIfGap]?: boolean;
  [BOOKING_BULK_FORM_FIELDS.conflictsDetailsCreateIfIneligible]?: string;
}

export interface IAddNewBulkBookingsForm extends IBulkSetBookingsForm {
  [BOOKING_ADD_NEW_FORM_FIELDS.passengers]: number[];
  [BOOKING_ADD_NEW_FORM_FIELDS.date]: TDateISO;
}

export const bookingConflictOverlappingTranslationKeysByConflictErrorTypeMap: Partial<
  Record<BookingConflictErrorType, string>
> = {
  [BookingConflictErrorType.overlappingBooking]: "conflictOverlappingBooking",
  [BookingConflictErrorType.gapSingleOverlappingBookingAToA]:
    "conflictOverlappingBooking",
  [BookingConflictErrorType.gapSingleOverlappingBookingBToA]:
    "conflictOverlappingBooking",
  [BookingConflictErrorType.gapSingleOverlappingBookingBToB]:
    "conflictOverlappingBooking",
};

export interface IBookingValidateErrors {
  errors: BookingConflictErrorType[];
}

export const isBookingEntity = (
  data: IExtendableDto | IBookingValidateErrors | undefined,
): data is BookingEntity => {
  if (data && "id" in data && !("errors" in data)) {
    return true;
  }

  return false;
};

export const isBookingErrors = (
  data: IExtendableDto | IBookingValidateErrors | undefined,
): data is IBookingValidateErrors => {
  if (data && "errors" in data) {
    return true;
  }

  return false;
};

export interface IBookingsPassengerData {
  bookingsMap: Map<number, BookingDto>;
  passengersBookingsMap: Map<number, Set<number>>;
}

export type TBookingFieldName =
  | "id"
  | "status"
  | "bookTime"
  | "direction"
  | "shiftId"
  | "originStationId"
  | "destinationStationId"
  | "approver"
  | "metadata.tags";

export const BookingFormFields = {
  id: "id",
  direction: "direction",
  bookTime: "bookTime",
  status: "status",
  shiftId: "shiftId",
  originStationId: "originStationId",
  destinationStationId: "destinationStationId",
  approver: "approver",
  tags: "metadata.tags",
} as const satisfies Record<string, TBookingFieldName>;
